import { Box, Button, TextField, Tooltip, Typography } from "@mui/material"
import { Ban, ShieldCheck } from "lucide-react"
import { useState, type JSX } from "react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import usePermissions from "@crm-src/hooks/usePermissions"
import { resellersApiService } from "@crm-src/lib/resellers-api"
import { resellerIsActive, type Reseller } from "@crm-src/types/lead"

interface ResellerActivationControlProps {
  reseller: Reseller
  onChanged: (reseller: Reseller) => void
}

/**
 * The COMPANY account switch.
 *
 * WHAT THIS ACTUALLY DOES, in one sentence, because the button is small and the
 * consequence is not: deactivating signs every person from this company out of
 * the reseller portal and stops them signing back in — immediately, not at the
 * end of some session. crm-api enforces it in ONE place
 * (`ResellerMembership.active_for`), which is why there is no separate "revoke
 * everyone" step to remember afterwards.
 *
 * The confirm dialog says that in words, and names the child-company count,
 * because "deactivate the parent" reads like it should take the branch with it
 * and it deliberately does not (spec §4.5).
 *
 * Gated on `canManageResellers()` — `crm:resellers` + `crm:edit`, which is
 * "an admin, or the salesperson this reseller is attached to". crm-api enforces
 * the same conjunction plus own-scope independently; this only hides a control
 * the server would refuse anyway.
 */
export function ResellerActivationControl({
  reseller,
  onChanged,
}: ResellerActivationControlProps): JSX.Element | null {
  const { toast } = useToast()
  const { canManageResellers } = usePermissions()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState("")
  const active = resellerIsActive(reseller)

  if (!canManageResellers()) return null

  const memberWord = "Everyone at this company"
  const children = reseller.activation?.child_company_count ?? 0

  const apply = async (): Promise<void> => {
    try {
      const updated = await resellersApiService.setActivation(reseller.id, !active, reason)
      onChanged(updated)
      setReason("")
      toast({
        title: active ? "Account deactivated" : "Account reactivated",
        description: active
          ? `${reseller.name} can no longer sign in to the reseller portal.`
          : `${reseller.name} can sign in to the reseller portal again.`,
      })
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data?.error ||
        (error as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0] ||
        "Failed to change the account state"
      toast({ variant: "destructive", title: "Error", description: message })
    }
  }

  return (
    <>
      {/* `describeChild`: by default MUI copies a Tooltip's title onto the child
          as `aria-label`, which REPLACES the button's own name — a screen
          reader announced the whole tooltip sentence and never "Deactivate
          account". `describeChild` makes it `aria-describedby` instead, which
          is what a supplementary hint on an already-labelled control should
          be. Icon-only buttons are the opposite case and correctly keep the
          default: there the tooltip IS the name. */}
        <Tooltip
        describeChild
        title={
          active
            ? "Stop this reseller's people signing in to the reseller portal"
            : "Let this reseller's people sign in to the reseller portal again"
        }
      >
        <Button
          data-cy="reseller-activation-toggle"
          variant="outlined"
          color={active ? "error" : "success"}
          size="small"
          startIcon={active ? <Ban size={16} /> : <ShieldCheck size={16} />}
          onClick={() => setConfirming(true)}
        >
          {active ? "Deactivate account" : "Reactivate account"}
        </Button>
      </Tooltip>

      <ConfirmDialog
        isOpen={confirming}
        onClose={() => {
          setConfirming(false)
          setReason("")
        }}
        title={active ? "Deactivate reseller account" : "Reactivate reseller account"}
        variant={active ? "destructive" : "default"}
        confirmLabel={active ? "Deactivate" : "Reactivate"}
        description={
          active
            ? `${memberWord} will be signed out of the reseller portal and cannot sign back in. Their accounts and permissions are kept, so reactivating restores exactly the access they have now.`
            : `${memberWord} will be able to sign in to the reseller portal again, with the access they had before.`
        }
        onConfirm={apply}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {active && children > 0 && (
            <Typography data-cy="reseller-activation-children" sx={{ fontSize: 13, color: "warning.dark" }}>
              {children === 1
                ? "1 child company stays active — deactivating this one does not affect it."
                : `${children} child companies stay active — deactivating this one does not affect them.`}
            </Typography>
          )}

          {active && (
            <TextField
              label="Reason (optional)"
              size="small"
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. contract terminated"
              helperText="Recorded on the Activity tab so the next person can see why."
              data-cy="reseller-activation-reason"
            />
          )}
        </Box>
      </ConfirmDialog>
    </>
  )
}
